import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { FilterOption, TaskAggregateGroupBy, ActorRole } from "@gw/shared-types";
import { useTaskAggregateQuery } from "./useTaskAggregateQuery";
import { useTaskInstancesQuery } from "./useTaskInstancesQuery";
import { useRequestListQuery } from "@/features/requestList/useRequestListQuery";
import { useTaskEfficiencyUrlState } from "./useTaskEfficiencyUrlState";
import { TASK_DEFAULTS, type TaskEfficiencyState } from "./taskEfficiencySchema";
import { GROUP_LABEL_BY_KEY, GroupByChips } from "./components/GroupByChips";
import { AggregateTable } from "./components/AggregateTable";
import { TaskInstancesTable } from "./components/TaskInstancesTable";
import { TabSwitcher } from "./components/TabSwitcher";
import { DrillChips } from "./components/DrillChips";
import { TaskFilterRail } from "./components/TaskFilterRail";
import { SearchInput } from "@/features/requestList/components/SearchInput";
import { Pagination, type PageSize } from "@/features/requestList/components/Pagination";
import type { SortState } from "@/features/requestList/components/SortHead";
import type {
  AggregateTasksParams,
  ListTaskInstancesParams,
} from "@/api/endpoints";
import { TableSkeleton } from "@/components/Skeleton";
import { BrandedLoader } from "@/components/BrandedLoader";
import { useDelayedFlag } from "@/lib/useDelayedFlag";
import { UnitToggle } from "@/components/UnitToggle";
import { AssistantPanel } from "@/features/assistant/AssistantPanel";

// Static actor-role options — fixed enum, no need to derive from data.
const ACTOR_ROLE_OPTIONS: FilterOption[] = (
  ["Consultant", "SubvendorWorker", "SubvendorOrg", "ClientService", "AccountManager", "Internal", "Other"] as ActorRole[]
).map((v) => ({ value: v, count: 0 }));

function resolveDateWindow(state: TaskEfficiencyState): { from: string; to: string } {
  let from = state.from;
  let to = state.to;
  if (!from || !to) {
    const now = new Date();
    from = new Date(now.getTime() - state.dateRange * 86_400_000).toISOString();
    to = now.toISOString();
  }
  return { from, to };
}

function buildFilterParams(state: TaskEfficiencyState) {
  return {
    onboardingsOnly: state.onboardingsOnly,
    redeployment: state.redeployment,
    requestType: state.requestType,
    status: state.status,
    client: state.client,
    lob: state.lob,
    eng: state.eng,
    assigned: state.assigned,
    country: state.country,
    bucket: state.bucket,
    rework: state.rework,
    taskCategory: state.taskCategory,
    taskName: state.taskName,
    actorRole: state.actorRole,
    taskStatus: state.taskStatus,
    q: state.q || undefined,
    qPills: state.qPills.length ? state.qPills : undefined,
  };
}

function applyDrilldownToFilters(
  filterParams: ReturnType<typeof buildFilterParams>,
  drillKeys: TaskAggregateGroupBy[],
  drillVals: string[],
) {
  // Drilldown is conceptually an additional filter on the group keys. Translate the keys to
  // their filter dimension and intersect with the user's selection.
  const next = { ...filterParams };
  for (let i = 0; i < drillKeys.length; i++) {
    const key = drillKeys[i]!;
    const val = drillVals[i]!;
    switch (key) {
      case "client":
        next.client = [val];
        break;
      case "lineOfBusiness":
        next.lob = [val];
        break;
      case "engagementType":
        next.eng = [val];
        break;
      case "assignedTo":
        next.assigned = [val];
        break;
      case "taskCategory":
        next.taskCategory = [val];
        break;
      case "taskName":
        next.taskName = [val];
        break;
      // taskType, templateName: not in the filter rail; skip — server ignores unknown filters.
      default:
        break;
    }
  }
  return next;
}

export function TaskEfficiencyScreen() {
  const [state, setState] = useTaskEfficiencyUrlState();
  const navigate = useNavigate();

  const dateWindow = useMemo(() => resolveDateWindow(state), [state]);

  // Filter options come from the request snapshot — we piggyback on the request list endpoint
  // with no filters and the same date range. Request snapshot is already cached server-side.
  const { data: requestPage } = useRequestListQuery({
    page: 1,
    pageSize: 25,
    from: dateWindow.from,
    to: dateWindow.to,
  });
  const filterOptions = requestPage?.summary?.filterOptions;

  const aggregateParams: AggregateTasksParams = useMemo(
    () => ({
      groupBy: state.groupBy,
      from: dateWindow.from,
      to: dateWindow.to,
      minSample: state.minSample,
      unit: state.durationUnit,
      ...buildFilterParams(state),
    }),
    [state, dateWindow],
  );

  const instancesParams: ListTaskInstancesParams = useMemo(() => {
    const filters = applyDrilldownToFilters(
      buildFilterParams(state),
      state.drillKeys,
      state.drillVals,
    );
    return {
      page: state.page,
      pageSize: state.pageSize,
      from: dateWindow.from,
      to: dateWindow.to,
      sort: state.instanceSort,
      dir: state.instanceDir,
      unit: state.durationUnit,
      ...filters,
    };
  }, [state, dateWindow]);

  const aggregateQuery = useTaskAggregateQuery(aggregateParams);
  const instancesQuery = useTaskInstancesQuery(instancesParams, state.tab === "instances");

  // Derive task-category / task-name options from the current aggregate response when available.
  // Acceptable degradation: when the aggregate is grouped on these dimensions, the options
  // already cover the full snapshot; otherwise we fall back to whatever the rows expose.
  const taskCategoryOptions = useMemo<FilterOption[]>(() => {
    if (!aggregateQuery.data) return [];
    const counts = new Map<string, number>();
    for (const row of aggregateQuery.data) {
      const v = row.groupValues["taskCategory"];
      if (!v) continue;
      counts.set(v, (counts.get(v) ?? 0) + row.sampleSize);
    }
    return [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
  }, [aggregateQuery.data]);

  const taskNameOptions = useMemo<FilterOption[]>(() => {
    if (!aggregateQuery.data) return [];
    const counts = new Map<string, number>();
    for (const row of aggregateQuery.data) {
      const v = row.groupValues["taskName"];
      if (!v) continue;
      counts.set(v, (counts.get(v) ?? 0) + row.sampleSize);
    }
    return [...counts.entries()]
      .map(([value, count]) => ({ value, count }))
      .sort((a, b) => b.count - a.count);
  }, [aggregateQuery.data]);

  const handleAggSortChange = (s: SortState) => setState({ sort: s.key, dir: s.dir });
  const handleInstanceSortChange = (s: SortState) =>
    setState({ instanceSort: s.key, instanceDir: s.dir, page: 1 });

  const handleDrillIn = (groupKeys: TaskAggregateGroupBy[], vals: string[]) => {
    setState({
      tab: "instances",
      drillKeys: groupKeys,
      drillVals: vals,
      page: 1,
    });
  };

  const handleClearDrill = () => setState({ drillKeys: [], drillVals: [], page: 1 });

  const handleClearAll = () => {
    setState({
      requestType: [],
      status: [],
      client: [],
      lob: [],
      eng: [],
      assigned: [],
      country: [],
      bucket: [],
      rework: false,
      onboardingsOnly: false,
      redeployment: false,
      taskCategory: [],
      taskName: [],
      actorRole: [],
      taskStatus: [],
      from: undefined,
      to: undefined,
      dateRange: TASK_DEFAULTS.dateRange,
      drillKeys: [],
      drillVals: [],
      page: 1,
    });
  };

  const drillLabel =
    state.drillKeys.length > 0
      ? state.drillKeys
          .map((k, i) => `${GROUP_LABEL_BY_KEY[k]}: ${state.drillVals[i]}`)
          .join(" / ")
      : undefined;

  // Cover the content with the branded loader while the active tab's data is in flight, so partial
  // filters/tables never show. Gated so fast warm-cache loads don't flash it.
  const fetching =
    state.tab === "instances" ? instancesQuery.isFetching : aggregateQuery.isFetching;
  const showLoader = useDelayedFlag(fetching);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Tasks</h1>
          <div className="subtitle">
            Identify systematically slow tasks across the business.
          </div>
        </div>
        <div className="actions">
          <UnitToggle
            value={state.durationUnit}
            onChange={(u) => setState({ durationUnit: u })}
          />
        </div>
      </div>

      <div className="screen-body">
        {showLoader && <BrandedLoader />}

        <div className="list-layout">
        <div className="rail-dock">
        <TaskFilterRail
          state={state}
          setState={setState}
          filterOptions={filterOptions ?? undefined}
          taskCategoryOptions={taskCategoryOptions}
          taskNameOptions={taskNameOptions}
          actorRoleOptions={ACTOR_ROLE_OPTIONS}
          onClearAll={handleClearAll}
        />
        <AssistantPanel screen="taskEfficiency" activeFilters={state} />
        </div>

        <div className="card">
          <div className="toolbar">
            <SearchInput
              value={state.q}
              onChange={(q) => setState({ q, page: 1 })}
              onCommit={(text) => {
                // Dedupe case-insensitively so re-typing the same word doesn't pile up pills.
                const existing = state.qPills.find(
                  (p) => p.toLowerCase() === text.toLowerCase(),
                );
                setState({
                  q: "",
                  qPills: existing ? state.qPills : [...state.qPills, text],
                  page: 1,
                });
              }}
              placeholder="Search task name, category, worker, client — Enter to pin as filter"
              ariaLabel="Search tasks"
            />
          </div>
          {state.qPills.length > 0 && (
            <div
              className="chip-row"
              style={{ padding: "8px 12px", borderBottom: "1px solid var(--border-subtle)" }}
            >
              {state.qPills.map((pill) => (
                <span
                  key={`qPill:${pill}`}
                  className="chip"
                  onClick={() =>
                    setState({ qPills: state.qPills.filter((p) => p !== pill), page: 1 })
                  }
                >
                  &ldquo;{pill}&rdquo; <span className="x">×</span>
                </span>
              ))}
            </div>
          )}
          <TabSwitcher
            tab={state.tab}
            onChange={(tab) => setState({ tab })}
            drillLabel={drillLabel}
          />

          {state.tab === "aggregate" && (
            <>
              <GroupByChips
                selected={state.groupBy}
                onChange={(keys) => setState({ groupBy: keys })}
              />

              {aggregateQuery.isLoading ? (
                <TableSkeleton cols={10} rows={8} />
              ) : aggregateQuery.isError || !aggregateQuery.data ? (
                <div className="empty">
                  <h4>Could not load aggregates</h4>
                  <p>{(aggregateQuery.error as Error)?.message ?? "Unknown error"}</p>
                </div>
              ) : (
                <>
                  <AggregateTable
                    rows={aggregateQuery.data}
                    groupBy={state.groupBy}
                    sort={{ key: state.sort, dir: state.dir }}
                    onSortChange={handleAggSortChange}
                    unit={state.durationUnit}
                    onRowClick={(row) =>
                      handleDrillIn(
                        state.groupBy,
                        state.groupBy.map((k) => row.groupValues[k] ?? ""),
                      )
                    }
                  />
                  <div className="pagination">
                    <div>
                      {aggregateQuery.data.length} groups ·{" "}
                      {aggregateQuery.data
                        .reduce((sum, r) => sum + r.sampleSize, 0)
                        .toLocaleString()}{" "}
                      task instances total
                    </div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontSize: 11,
                        color: "var(--fg-3)",
                      }}
                    >
                      <span>Min sample size</span>
                      <input
                        type="number"
                        min={1}
                        max={50}
                        value={state.minSample}
                        onChange={(e) =>
                          setState({ minSample: Math.max(1, Number(e.target.value) || 1) })
                        }
                        style={{
                          width: 56,
                          padding: "2px 6px",
                          border: "1px solid var(--border-subtle)",
                          borderRadius: 4,
                          fontSize: 12,
                        }}
                      />
                      <span style={{ marginLeft: 8 }}>· Click any row to drill in</span>
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {state.tab === "instances" && (
            <>
              <DrillChips
                drillKeys={state.drillKeys}
                drillVals={state.drillVals}
                onClear={handleClearDrill}
              />

              {instancesQuery.isLoading ? (
                <TableSkeleton cols={11} rows={8} />
              ) : instancesQuery.isError || !instancesQuery.data ? (
                <div className="empty">
                  <h4>Could not load instances</h4>
                  <p>{(instancesQuery.error as Error)?.message ?? "Unknown error"}</p>
                </div>
              ) : (
                <>
                  <TaskInstancesTable
                    rows={instancesQuery.data.items}
                    sort={{ key: state.instanceSort, dir: state.instanceDir }}
                    onSortChange={handleInstanceSortChange}
                    unit={state.durationUnit}
                    onRowClick={(requestId) =>
                      navigate(
                        `/requests/${requestId}?from=${encodeURIComponent(dateWindow.from)}&to=${encodeURIComponent(dateWindow.to)}`,
                      )
                    }
                  />
                  <Pagination
                    page={state.page}
                    pageSize={state.pageSize}
                    totalCount={instancesQuery.data.totalCount}
                    totalPages={instancesQuery.data.totalPages}
                    onPageChange={(p) => setState({ page: p })}
                    onPageSizeChange={(ps: PageSize) => setState({ pageSize: ps, page: 1 })}
                  />
                </>
              )}
            </>
          )}
        </div>
        </div>
      </div>
    </>
  );
}

